import { type ReactNode, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Stepper } from '../../../../components/Stepper';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { DateField } from '../../../../components/inputs/DateField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi } from '../personnelApi';
import {
  EMPLOYMENT_TYPE_LABELS,
  type EmploymentType,
  type PersonnelWorkExperience,
} from '../personnelTypes';
import { sampleWorkExperience } from '../utils/sampleWorkExperience';

/** Unanswered is a third state, and the form refuses to save in it. */
type GovernmentAnswer = '' | 'yes' | 'no';

type StepKey = 'service' | 'details' | 'review';

interface Step {
  key: StepKey;
  label: string;
  valid: boolean;
  content: ReactNode;
}

/**
 * Records or corrects one position held — CS Form 212, section V.
 *
 * **No picker on this form reads a catalog, and that is the design rather than an omission.**
 * The position title, the employer, the salary grade and the step are typed, because this row
 * records what an employee stated about an employer — usually not this one. Binding the grade
 * to the salary schedule would re-price a fifteen-year-old job every time a new tranche is
 * published, and binding the title to the position catalog would attach our qualification
 * standards to somebody else's job. The field descriptions say so on the form, because a
 * reader who expects a dropdown and meets a text box should be told why.
 *
 * The one exception is the status of appointment, which is a `<Select>` over the product's
 * existing `EmploymentType` vocabulary — a fixed set of words, not a link to a row.
 *
 * **The CREATE form is stepped; the EDIT form is not.** Whether the position was government
 * service frames everything after it — it is the column length of service is counted off — so
 * it is asked on its own, before the reader starts typing an employer's name. Correcting a
 * stored row is the opposite job: CLAUDE.md is explicit that opening a wizard from a card
 * "re-asks every question to change one answer", and this modal IS that card's dedicated
 * edit. So `editing` decides, and the flat form is unchanged for it.
 */
export function EditWorkExperienceModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelWorkExperience | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [periodFrom, setPeriodFrom] = useState(editing?.periodFrom ?? '');
  const [periodTo, setPeriodTo] = useState(editing?.periodTo ?? '');
  const [positionTitle, setPositionTitle] = useState(editing?.positionTitle ?? '');
  const [agencyName, setAgencyName] = useState(editing?.agencyName ?? '');
  const [monthlySalary, setMonthlySalary] = useState(
    editing?.monthlySalary === null || editing?.monthlySalary === undefined
      ? ''
      : String(editing.monthlySalary),
  );
  const [salaryGrade, setSalaryGrade] = useState(editing?.salaryGrade ?? '');
  const [stepIncrement, setStepIncrement] = useState(editing?.stepIncrement ?? '');
  const [appointmentStatus, setAppointmentStatus] = useState<EmploymentType | null>(
    editing?.appointmentStatus ?? null,
  );

  /*
    Seeded EMPTY on a create, never to "no". False is not a neutral default on this field — it
    is the claim that the position was private employment, and length of government service is
    counted from these rows. The server refuses an unanswered request for the same reason.
  */
  const [isGovernmentService, setIsGovernmentService] = useState<GovernmentAnswer>(
    editing === null ? '' : editing.isGovernmentService ? 'yes' : 'no',
  );

  const [step, setStep] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /** Create is stepped; correcting a stored row is not. See the component's doc comment. */
  const stepped = editing === null;

  const today = todayCalendarDate();

  const salaryValue = monthlySalary.trim() === '' ? null : Number(monthlySalary);
  const salaryIsNumber = salaryValue === null || Number.isFinite(salaryValue);
  const salaryNotNegative = salaryValue === null || salaryValue >= 0;

  const endsBeforeStart = periodTo !== '' && periodFrom !== '' && periodTo < periodFrom;

  const serviceValid = isGovernmentService !== '';

  const detailsValid =
    positionTitle.trim().length > 0 &&
    agencyName.trim().length > 0 &&
    periodFrom.length > 0 &&
    appointmentStatus !== null &&
    salaryIsNumber &&
    salaryNotNegative &&
    !endsBeforeStart;

  const valid = serviceValid && detailsValid;

  /*
    Both field blocks are built once and placed by whichever shape is rendering — the stepped
    create puts them on their own steps, the flat edit stacks them in the run. Writing the
    fields out twice is the drift the golden rule names: a description corrected on one copy
    and not the other is invisible until somebody screenshots both.
  */
  const serviceFields = (
    <RadioGroup<GovernmentAnswer>
      id="work-experience-is-government-service"
      label="Government service"
      required
      description="Yes if the position held was in public or government employment, no if it was private."
      /*
        No default. An unanswered field is a third state here, and it has to stay
        answerable-but-unanswered until the reader chooses: defaulting to No would record
        private employment on every row nobody looked at, and years of government service
        would quietly go missing.
      */
      value={isGovernmentService}
      onChange={setIsGovernmentService}
      options={[
        {
          value: 'yes',
          label: 'Yes',
          description: 'Public or government employment.',
        },
        {
          value: 'no',
          label: 'No',
          description: 'Private employment.',
        },
      ]}
      error={
        isGovernmentService === '' ? 'Choose yes or no.' : error?.fieldError('isGovernmentService')
      }
      disabled={saving}
    />
  );

  const detailsFields = (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="work-experience-position-title"
          label="Position title"
          required
          description="Written in full, as it was held. Not a title from the position catalog — this records another employer's job."
          value={positionTitle}
          onChange={(e) => setPositionTitle(e.target.value)}
          error={
            positionTitle.trim() ? error?.fieldError('positionTitle') : 'Enter the position title.'
          }
          disabled={saving}
        />

        <TextField
          id="work-experience-agency-name"
          label="Department, agency, office or company"
          required
          description="Written in full, as the form asks."
          value={agencyName}
          onChange={(e) => setAgencyName(e.target.value)}
          error={agencyName.trim() ? error?.fieldError('agencyName') : 'Enter the employer.'}
          disabled={saving}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          id="work-experience-period-from"
          label="From"
          required
          max={today}
          value={periodFrom}
          onChange={setPeriodFrom}
          error={periodFrom ? error?.fieldError('periodFrom') : 'Choose the day it began.'}
          disabled={saving}
        />

        <DateField
          id="work-experience-period-to"
          label="To"
          max={today}
          description="Leave it empty if this position is still held."
          value={periodTo}
          onChange={setPeriodTo}
          error={
            endsBeforeStart
              ? 'Choose a date on or after the day it began.'
              : error?.fieldError('periodTo')
          }
          disabled={saving}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="work-experience-salary-grade"
          label="Salary Grade, job or pay grade"
          /*
            The description carries the independence, because the field looks like it should
            be a picker and is not. A reader who assumes this is the salary schedule would
            wonder why their grade is not in a dropdown; saying it outright is cheaper than
            the support question.
          */
          description="Text, as the employer expressed it — 24, Band B. Not linked to the salary schedule: a grade only means something alongside the schedule that defined it, and that one has moved since."
          value={salaryGrade}
          onChange={(e) => setSalaryGrade(e.target.value)}
          error={error?.fieldError('salaryGrade')}
          disabled={saving}
        />

        <TextField
          id="work-experience-step-increment"
          label="Step or increment"
          description="The step within that grade — 1, Step 3."
          value={stepIncrement}
          onChange={(e) => setStepIncrement(e.target.value)}
          error={error?.fieldError('stepIncrement')}
          disabled={saving}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="work-experience-monthly-salary"
          label="Monthly salary"
          type="number"
          min={0}
          step="0.01"
          description="What was actually paid, as stated. Never read from the salary schedule."
          value={monthlySalary}
          onChange={(e) => setMonthlySalary(e.target.value)}
          error={
            !salaryIsNumber
              ? 'Enter a monthly salary.'
              : !salaryNotNegative
                ? 'Enter a monthly salary of 0 or more.'
                : error?.fieldError('monthlySalary')
          }
          disabled={saving}
        />

        <Select
          id="work-experience-appointment-status"
          label="Status of appointment"
          required
          description="How the position was held — permanent, temporary, casual, contractual."
          value={appointmentStatus === null ? null : String(appointmentStatus)}
          onChange={(next) =>
            setAppointmentStatus(next === null ? null : (Number(next) as EmploymentType))
          }
          options={Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => ({
            value,
            label,
          }))}
          /*
            The form's own order, not alphabetical: permanent, temporary, casual, contractual
            — which also runs from most to least secure tenure. Re-sorting it would scatter
            that and put Casual above Permanent.
          */
          sort="none"
          error={
            appointmentStatus === null
              ? 'Choose a status of appointment.'
              : error?.fieldError('appointmentStatus')
          }
          disabled={saving}
        />
      </div>
    </div>
  );

  const steps: Step[] = [
    { key: 'service', label: 'Service', valid: serviceValid, content: serviceFields },
    { key: 'details', label: 'Details', valid: detailsValid, content: detailsFields },
    {
      key: 'review',
      label: 'Review',
      // Always valid: the step has no fields, so gating it would be gating on nothing.
      valid: true,
      content: (
        /*
          Read-only facts, so the Table Display View — a tinted key against a plain value.
          Every row renders whatever the value: a missing fact is an em dash, because a
          dropped row would make the summary a different shape per record and the reader
          could not tell "not collected" from "does not apply".
        */
        <PreviewTable
          variant="table-display"
          rows={[
            {
              label: 'Government service',
              value:
                isGovernmentService === '' ? null : isGovernmentService === 'yes' ? 'Yes' : 'No',
            },
            { label: 'Position title', value: positionTitle.trim() || null },
            { label: 'Department, agency, office or company', value: agencyName.trim() || null },
            { label: 'From', value: periodFrom ? formatDateOnly(periodFrom) : null, mono: true },
            {
              /*
                "Present", not an em dash — a row with no end date is the form's own PRESENT, a
                real answer saying the position is still held. The panel renders it that way
                too, so the summary and the row the reader lands on afterwards agree.
              */
              label: 'To',
              value: periodTo ? formatDateOnly(periodTo) : 'Present',
              mono: true,
            },
            {
              label: 'Salary Grade, job or pay grade',
              value: salaryGrade.trim() || null,
              mono: true,
            },
            { label: 'Step or increment', value: stepIncrement.trim() || null, mono: true },
            {
              // Null rather than formatMoney(null), so the component draws the em dash.
              label: 'Monthly salary',
              value: salaryValue === null ? null : formatMoney(salaryValue),
              mono: true,
            },
            {
              label: 'Status of appointment',
              value: appointmentStatus === null ? null : EMPLOYMENT_TYPE_LABELS[appointmentStatus],
            },
          ]}
        />
      ),
    },
  ];

  const stepIndex = Math.min(step, steps.length - 1);
  const isLastStep = stepIndex === steps.length - 1;

  /** The flat edit form has no last step to reach, so the tick is always offered there. */
  const confirmShowing = !stepped || isLastStep;

  const canSave = valid && confirmed && !saving;

  /** Fills every required field, so the primary action enables on the first try. */
  function generate() {
    const sample = sampleWorkExperience();

    setPeriodFrom(sample.periodFrom);
    setPeriodTo(sample.periodTo);
    setPositionTitle(sample.positionTitle);
    setAgencyName(sample.agencyName);
    setMonthlySalary(sample.monthlySalary);
    setSalaryGrade(sample.salaryGrade);
    setStepIncrement(sample.stepIncrement);
    setAppointmentStatus(sample.appointmentStatus);
    setIsGovernmentService(sample.isGovernmentService);
  }

  /**
   * Which step a server-named field lives on, so a rejection can move the reader to it.
   *
   * Only `isGovernmentService` is on step one; every other field the server can name is on
   * step two. The review step names none, because it collects none.
   */
  function stepOf(field: string): number {
    return field === 'isGovernmentService' ? 0 : 1;
  }

  async function save() {
    /*
      No second check for an unanswered `isGovernmentService` here: `canSave` already carries
      it, and TypeScript narrows the value off that — a `=== ''` test below this line is dead
      code the compiler refuses outright. The check lives in `valid`, where every other
      required field's does.
    */
    if (!canSave || appointmentStatus === null) return;

    setSaving(true);
    setError(null);

    const request = {
      periodFrom,

      // Empty is the form's PRESENT — a real answer meaning the position is still held, so it
      // travels as null rather than as today's date.
      periodTo: periodTo || null,
      positionTitle: positionTitle.trim(),
      agencyName: agencyName.trim(),
      monthlySalary: salaryValue,
      salaryGrade: salaryGrade.trim() || null,
      stepIncrement: stepIncrement.trim() || null,
      appointmentStatus,
      isGovernmentService: isGovernmentService === 'yes',
    };

    try {
      if (editing) await personnelApi.updateWorkExperience(personnelId, editing.id, request);
      else await personnelApi.addWorkExperience(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      /*
        Every field kebabs straight onto its own input id — `positionTitle` becomes
        `work-experience-position-title` — with ONE exception that needs the map.

        `<RadioGroup>` gives each option its own id (`…-is-government-service-yes` / `-no`) and
        the fieldset carries none, so the derived id resolves to nothing: the reader would be
        told which field was wrong and nothing would move. That is the case CLAUDE.md keeps
        `fieldIds` for — a control the rename rule cannot reach. Focus lands on the first
        option, which is where a keyboard reader enters a radio group anyway.

        `stepOf`/`onStep` go only while stepped: a field is not in the DOM until its step
        renders, and on the flat edit form every one of them already is.
      */
      focusValidationError(failure, {
        idPrefix: 'work-experience',
        fieldIds: { isGovernmentService: 'work-experience-is-government-service-yes' },
        ...(stepped ? { stepOf, onStep: setStep } : {}),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit work experience' : 'Add work experience'}
      size="2xl"
      id="work-experience-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      /*
        LAST STEP ONLY while stepped. The tick certifies the whole transaction, so offering it
        on step one invites confirming a form the reader has not seen the rest of — and the
        strip appearing IS the signal that there is nothing left to fill in.
      */
      confirm={
        confirmShowing
          ? {
              id: 'work-experience-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving,
            }
          : undefined
      }
      footer={
        <>
          <Button
            id="work-experience-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          {/*
            Back sits next to Next — the pair that moves through the form — and renders from
            the second step on, exactly as Next stops rendering on the last. It is never gated
            on the step being valid: going back is how a reader FIXES an invalid step, so
            gating it would strand them on the one they came to correct.
          */}
          {stepped && stepIndex > 0 ? (
            <Button
              id="work-experience-back"
              variant="secondary"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {stepped && !isLastStep ? (
            <Button
              id="work-experience-next"
              variant="secondary"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={!steps[stepIndex].valid}
            >
              Next
            </Button>
          ) : null}

          <Button
            id="work-experience-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {stepped ? (
          <>
            <Stepper
              id="work-experience"
              steps={steps.map(({ key, label }) => ({ key, label }))}
              activeIndex={stepIndex}
              onJump={(index) => {
                // Forward movement requires every earlier step to be valid; back is free.
                if (index <= stepIndex || steps.slice(0, index).every((s) => s.valid)) {
                  setStep(index);
                }
              }}
            />

            <div className="pt-2">{steps[stepIndex].content}</div>
          </>
        ) : (
          <>
            {serviceFields}
            {detailsFields}
          </>
        )}
      </div>
    </Modal>
  );
}
